import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@/theme/colors';
import ResourceLink from './ResourceLink';

interface ChildrenProps {
  children: ReactNode;
}

export const GridItem: React.FC<ChildrenProps> = ({ children }) => (
  <Grid item xs={12} sm={6} lg={4} data-testid="grid-item">
    {children}
  </Grid>
);

export const InfoRow: React.FC<ChildrenProps> = ({ children }) => (
  <Box
    sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}
    data-testid="info-row"
  >
    {children}
  </Box>
);

export const FrameRow: React.FC<
  ChildrenProps & { href?: string; title?: string }
> = ({ children, href, title }) => {
  const { t } = useTranslation(['resources']);
  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'row', mb: 2, gap: 1 }}
      data-testid="frame-row"
    >
      <Typography variant="body2" component="span" data-testid="frame-label">
        {t('frameColon')}
      </Typography>
      {href && title ? (
        <ResourceLink href={href} title={title} />
      ) : (
        <Typography
          variant="body2"
          component="span"
          fontWeight={500}
          data-testid="frame-text"
        >
          {children}
        </Typography>
      )}
    </Box>
  );
};

interface InfoLabelProps {
  children: ReactNode;
}

export const RowIcon: React.FC<{ icon: ReactNode }> = ({ icon }) => (
  <Box
    data-testid="row-icon"
    sx={{
      mr: 1,
      display: 'flex',
      alignItems: 'center',
      color: colors.textGrey,
    }}
  >
    {icon}
  </Box>
);

export const InfoLabel: React.FC<InfoLabelProps> = ({ children }) => (
  <Typography
    variant="body2"
    component="span"
    data-testid="info-label"
    sx={{
      color: 'text.secondary',
      fontWeight: 500,
    }}
  >
    {children}
  </Typography>
);

export const InfoValue: React.FC<ChildrenProps> = ({ children }) => (
  <Typography
    variant="body2"
    component="span"
    data-testid="info-value"
    sx={{
      color: 'text.primary',
    }}
  >
    {children}
  </Typography>
);
